/* eslint-disable camelcase */
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client'
import {
  FamilyMemberInput,
  MutationAddFamilyMemberArgs,
  MutationEditFamilyMemberArgs,
  MutationApplyCodeArgs,
} from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import {
  ButtonSimple,
  ButtonVariant,
  useModalActions,
  Profile,
  Text as MedkitText,
} from '@babylon/medkit'
import {
  Button,
  VStack,
  CardList,
  useDisclosure,
  Spacer,
  Text as DNAText,
  Box,
} from '@babylon/web-platform-ui'
import {
  AddFamilyMemberResponse,
  ApplyCodeResponse,
  EditFamilyMemberProps,
  EditFamilyMemberResponse,
  FamilyMembersResponse,
} from '@/types'
import {
  shouldSkipRedemptionErrors,
  isUserEligible as checkUserEligibility,
} from '@/utils'

import {
  AddOrEditFamilyMemberModal,
  AddOrEditFamilyMemberModalUseDnaV2Design,
  FamilyMemberCard,
  EligibilityModal,
  EligibilityModalUseDnaV2Design,
} from '@/components'
import AddFamilyMemberMutation from '@/mutations/addFamilyMember'
import EditFamilyMemberMutation from '@/mutations/editFamilyMember'
import ApplyCodeMutation from '@/mutations/applyCode'
import FamilyMembersQuery from '@/queries/FamilyMembers'
import { useNextAction } from '@/redux/signup/selectors'
import messages from './FamilyPage.messages'
import { trackTealiumEvent, trackTealiumView } from '@/tracking'
import { useUserConfig } from '@/redux/userConfig/selectors'
import { useUserId } from '@/redux/userProfile/selectors'
import { useConfig } from '@/redux/partnerConfig/selectors'
import { showMessage } from '@/redux/appMessage/actions'
import { useTrackingScreenTitle } from '@/hooks'

const Family = () => {
  const translate = useFormatMessage()
  const nextAction = useNextAction()
  const { membershipCode } = useUserConfig()
  const { id: partnerId, codes: { promo: promoCode } = {} } = useConfig()
  const { useDnaV2Design } = useConfig()
  const userId = useUserId()
  const { openModal, closeModal } = useModalActions()
  const addFamilyMemberModal = useDisclosure()
  const editFamilyMemberModal = useDisclosure()
  const eligibilityModalUseDnaV2 = useDisclosure()
  const dispatch = useDispatch()
  const {
    data: { familyMembers = [] } = {},
    loading: familyMembersLoading,
  } = useQuery<FamilyMembersResponse>(FamilyMembersQuery)

  const [
    currentlyEditingFamilyMember,
    setCurrentlyEditingFamilyMember,
  ] = useState<string | null>(null)

  const stepTrackingName = useTrackingScreenTitle()

  const [addFamilyMember] = useMutation<
    AddFamilyMemberResponse,
    MutationAddFamilyMemberArgs
  >(AddFamilyMemberMutation, {
    context: { needsClinicalAuth: true }, // TODO: What's this?
    refetchQueries: [{ query: FamilyMembersQuery }],
  })
  const [editFamilyMember] = useMutation<
    EditFamilyMemberResponse,
    MutationEditFamilyMemberArgs
  >(EditFamilyMemberMutation, {
    context: { needsClinicalAuth: true }, // TODO: What's this?
    refetchQueries: [{ query: FamilyMembersQuery }],
  })
  const [applyCode] = useMutation<ApplyCodeResponse, MutationApplyCodeArgs>(
    ApplyCodeMutation
  )

  const onClick = () => {
    nextAction()
  }

  const trackCloseFamilyModal = () => {
    trackTealiumView({
      screen_title: stepTrackingName,
      tealium_event: stepTrackingName,
    })
  }

  const onAddFamilyMemberFormSubmit = async (payload: FamilyMemberInput) => {
    let familyMemberId

    try {
      const { data } = await addFamilyMember({
        variables: {
          id: userId!,
          input: {
            ...payload,
            ...(membershipCode && {
              redemptions_attributes: [
                {
                  code: membershipCode,
                  redemption_type: 'hybrid',
                },
              ],
            }),
          },
        },
      })

      familyMemberId = data?.addFamilyMember?.id
    } catch (err) {
      // Show eligibility fail if we get a 422 error caused by an invalid code otherwise
      // throw error as before to show regular validation errors
      const errorMessage = JSON.parse(err.message)
      const notEligibleError =
        membershipCode && errorMessage?.response?.status === 422

      if (notEligibleError) {
        if (useDnaV2Design) {
          addFamilyMemberModal.onClose()
          eligibilityModalUseDnaV2.onOpen()
        } else {
          openModal(<EligibilityModal />, {}, true)
        }

        return
      }

      throw err
    }

    if (familyMemberId) {
      trackTealiumEvent({
        screen_title: 'signup/add-member',
        eventCategory: 'signup',
        eventAction: 'added family member',
        recipientId: familyMemberId,
        tealium_event: 'added family member',
      })

      try {
        if (promoCode) {
          await applyCode({
            variables: {
              code: promoCode,
              patient_id: Number(familyMemberId),
              redemption_type: 'hybrid',
            },
          })
        }
      } catch (error) {
        const errorMessage = JSON.parse(error.message)
        const errorData = errorMessage?.response?.data
        const shouldSkipMembershipError = shouldSkipRedemptionErrors(errorData)

        const isEligible = checkUserEligibility({
          errorData,
          isFamilyMember: true,
        })

        if (!isEligible) {
          const errorDetails = errorData?.error_details

          // We only want to display the eligibility modal if there are no more
          // errors. Also,if we only have the eligibility error, we don't want to
          // show any errors below the form as an information modal will appear to
          // provide more info to the user.
          if (Object.keys(errorDetails).length === 1) {
            if (useDnaV2Design) {
              addFamilyMemberModal.onClose()
              eligibilityModalUseDnaV2.onOpen()
            } else {
              openModal(<EligibilityModal />, {}, true)
            }

            return
          }
        }

        if (!shouldSkipMembershipError) {
          return
        }
      }
    }

    dispatch(showMessage(translate(messages.familyMemberAdded), 'positive'))

    trackCloseFamilyModal()
    closeModal()
    addFamilyMemberModal.onClose()
  }

  const onEditFamilyMemberFormSubmit = async (
    payload: FamilyMemberInput,
    id: number
  ) => {
    await editFamilyMember({
      variables: { input: payload, id },
    })

    trackTealiumEvent({
      screen_title: 'signup/edit-member',
      eventCategory: 'signup',
      eventAction: 'updated family member details',
      recipientId: id.toString(),
      tealium_event: 'updated family member details',
    })

    dispatch(showMessage(translate(messages.familyMemberEdited), 'positive'))

    trackCloseFamilyModal()
    closeModal()
    editFamilyMemberModal.onClose()
  }

  // V1 AddOrEditFamilyMemberModal
  const openFamilyMemberModal = (
    member?: EditFamilyMemberProps,
    id?: number
  ) => {
    if (id) {
      openModal(
        <AddOrEditFamilyMemberModal
          onSubmit={(payload) =>
            onEditFamilyMemberFormSubmit(payload, Number(id))
          }
          member={member}
          id={id}
        />,
        { onDismiss: trackCloseFamilyModal }
      )

      trackTealiumView({
        screen_title: 'signup/edit-member',
        tealium_event: 'signup/edit-member',
      })
    } else {
      openModal(
        <AddOrEditFamilyMemberModal onSubmit={onAddFamilyMemberFormSubmit} />,
        { onDismiss: trackCloseFamilyModal }
      )

      trackTealiumView({
        screen_title: 'signup/add-member',
        tealium_event: 'signup/add-member',
      })
    }
  }

  // V2 FamilyMemberModal
  const openFamilyMemberModalV2 = (id: string) => {
    setCurrentlyEditingFamilyMember(id)
    editFamilyMemberModal.onOpen()
  }

  const familyPageEmptySpace = familyMembers?.length ? 0 : 40

  const renderFamilyMembers = familyMembers.map((member) => {
    const {
      first_name,
      last_name,
      date_of_birth,
      id,
      email,
      gender,
      address_post_code,
    } = member
    const memberModalInformation: EditFamilyMemberProps = {
      first_name,
      last_name,
      date_of_birth,
      email,
      gender,
      address_post_code,
    }

    const onCardAction = () =>
      !useDnaV2Design
        ? openFamilyMemberModal(memberModalInformation, Number(id))
        : openFamilyMemberModalV2(id)

    return (
      <>
        <FamilyMemberCard
          key={id}
          onCardAction={onCardAction}
          member={memberModalInformation as EditFamilyMemberProps}
        />
        <AddOrEditFamilyMemberModalUseDnaV2Design
          isOpen={
            editFamilyMemberModal.isOpen && currentlyEditingFamilyMember === id
          }
          onClose={() => {
            trackCloseFamilyModal()
            editFamilyMemberModal.onClose()
          }}
          onSubmit={(payload) =>
            onEditFamilyMemberFormSubmit(payload, Number(id))
          }
          member={memberModalInformation}
          id={Number(id)}
        />
      </>
    )
  })

  return (
    <>
      {familyMembersLoading && (
        <Profile title="" noTopBorder noBottomBorder loading />
      )}
      {partnerId?.startsWith('ava') &&
        (useDnaV2Design ? (
          <Box>
            <DNAText>
              {translate(messages.ambetterDependentNoticeOver18)}
            </DNAText>
            <DNAText>
              {translate(messages.ambetterDependentNoticeUnder18)}
            </DNAText>
          </Box>
        ) : (
          <div>
            <MedkitText variant="body">
              {translate(messages.ambetterDependentNoticeOver18)}
            </MedkitText>
            <MedkitText variant="body">
              {translate(messages.ambetterDependentNoticeUnder18)}
            </MedkitText>
          </div>
        ))}
      {useDnaV2Design ? (
        <CardList mb={14} mt={10} data-testid="familyMemberCard">
          {renderFamilyMembers}
        </CardList>
      ) : (
        renderFamilyMembers
      )}
      {useDnaV2Design && (
        <Spacer marginTop={familyPageEmptySpace}>
          <VStack spacing="4" align="flex-start">
            <Button
              onClick={() => addFamilyMemberModal.onOpen()}
              variant="secondary"
              isFullWidth
              data-testid="form-submit-button"
            >
              {translate(messages.addFamilyMember)}
            </Button>
            <Button
              variant="primary"
              onClick={onClick}
              isFullWidth
              data-testid="form-next-button"
            >
              {translate(messages.done)}
            </Button>
            <AddOrEditFamilyMemberModalUseDnaV2Design
              isOpen={addFamilyMemberModal.isOpen}
              onClose={() => {
                trackCloseFamilyModal()
                addFamilyMemberModal.onClose()
              }}
              onSubmit={onAddFamilyMemberFormSubmit}
            />
          </VStack>
          <EligibilityModalUseDnaV2Design
            isOpen={eligibilityModalUseDnaV2.isOpen}
            onClose={eligibilityModalUseDnaV2.onClose}
          />
        </Spacer>
      )}
      {!useDnaV2Design && (
        <>
          <ButtonSimple
            onClick={() => openFamilyMemberModal()}
            variant={ButtonVariant.secondary}
            fullWidth
            data-testid="form-submit-button"
          >
            {translate(messages.addFamilyMember)}
          </ButtonSimple>
          <ButtonSimple
            onClick={onClick}
            variant={ButtonVariant.primary}
            fullWidth
            data-testid="form-next-button"
          >
            {translate(messages.done)}
          </ButtonSimple>
        </>
      )}
    </>
  )
}

export default Family
